/* eslint-disable react/react-in-jsx-scope */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listAllMonths } from "@/constants/constants";
import { useFetchData } from "@/hooks/useFetchData";
import { checkRate } from "@/libs/amount";
import { addMonths, isImage, isOffices } from "@/libs/utils";
import { Project } from "@/types/pp";
import { gsap } from "gsap";
import { ChevronLeft, ChevronRight, Info, TriangleAlert } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FieldInput from "./performance/FieldInput";
import OfficeThumbnail from "@/documents/components/OfficeThumbnail";
import PdfThumbnail from "@/documents/components/PdfThumbnail";
import ImageThumbnail from "@/documents/components/ImageThumbnail";
import { TreeRoot } from "@/types";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";

const PerformanceDetail = ({ projectData }: { projectData: Project }) => {
  // const data = JSON.parse(localStorage.getItem("project"))
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState("");
  const { data: treeFinancer, loading: loadingTreeFinancer } = useFetchDataV2<
    TreeRoot[]
  >(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/financer`,
    `tree-financer`,
    1000 * 60 * 60
  );
  const fetchConstraints = useFetchData(
    `${import.meta.env.VITE_IP_API}/media/constraints`
  );

  const [indicatorDataVerify, setIndicatorDataVerify] = useState(null);
  const [openModalVerify, setOpenModalVerify] = useState(false);
  const calendarRef = useRef(null);
  const [animating, setAnimating] = useState(false);
  const [year, setYear] = useState<number | null>(null);
  const [currencySelected, setCurrencySelected] = useState("EUR");
  const [indicators, setIndicators] = useState(null);
  const yearParam = searchParams.get("year");

  const launchDate = new Date(projectData?.launchDate).getFullYear();
  const launchMonthDate = new Date(projectData?.launchDate).getMonth();
  const expectedDuration = Number(projectData?.expectedDuration) || 0;
  const dateWithAddedMonths = addMonths(
    projectData?.launchDate as Date,
    expectedDuration
  );
  const expectedDurationYear = !dateWithAddedMonths
    ? 0
    : dateWithAddedMonths?.getFullYear() == launchDate
    ? 1
    : dateWithAddedMonths.getFullYear() - (launchDate - 1);
  const arrMonth = Array.from({ length: 12 }, (_, index) => index + 1);
  const arrYear = Array.from(
    { length: expectedDurationYear },
    (_, index) => index + 1
  );

  const [devises, setDevises] = useState([]);

  useEffect(() => {
    if (projectData?.launchDate) {
      setYear(new Date(projectData?.launchDate).getFullYear());
    }

    if (currencySelected === "EUR") {
      setIndicators(
        projectData?.indicators.map((ind) => {
          if (ind.unity.includes("|")) {
            const unities = ind.unity.split("|");
            return {
              ...ind,
              selectedCurrency: currencySelected,
              selectedUnity: unities[0],
            };
          }

          return {
            ...ind,
            selectedCurrency: currencySelected,
            selectedUnity: "",
          };
        })
      );
    }
  }, [projectData]);
  useEffect(() => {
    const devises = localStorage.getItem("devises");
    if (devises) {
      const devisesJSON = JSON.parse(devises);
      setDevises(devisesJSON);
    }
  }, []);

  const handleYearChange = (action: "prev" | "next") => {
    const currentYear = action === "prev" ? year - 1 : year + 1;
    animationCalendarView(currentYear);
  };

  const handleSelectYear = (value: string) => {
    const currentYear = Number(value);
    animationCalendarView(currentYear);
  };

  function animationCalendarView(currentYear: number) {
    if (animating) return;
    setAnimating(true);
    // Animation de sortie
    gsap.to(calendarRef.current, {
      x: currentYear > year ? -30 : 30, // Slide vers la gauche ou droite
      opacity: 0,
      duration: 0.3,
      ease: "power2.out",
      onComplete: () => {
        setYear(currentYear);

        // Réinitialiser la position pour l'animation d'entrée
        gsap.set(calendarRef.current, { x: currentYear > year ? 30 : -30 });

        // Animation d'entrée
        gsap.to(calendarRef.current, {
          x: 0,
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
          onComplete: () => setAnimating(false),
        });
      },
    });
    setSearchParams({
      year: currentYear.toString(),
      tab: "perf",
      id: projectData?.id,
    });
  }

  useEffect(() => {
    if (yearParam && launchDate && expectedDurationYear) {
      setYear(Number(yearParam));

      if (Number(yearParam) < launchDate) {
        setError(
          "L'année que vous avez renseigné dans l'url est inférieur à la date de lancement du projet"
        );
      }

      if (launchDate - 1 + expectedDurationYear < Number(yearParam)) {
        setError(
          "L'année que vous avez renseigné dans l'url est supérieur à la durée prévu du projet"
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (
    projectData?.launchDate == null ||
    projectData?.launchDate == undefined ||
    projectData?.expectedDuration == null ||
    projectData?.expectedDuration == undefined ||
    projectData?.expectedDuration == "0"
  ) {
    return (
      <p className="mt-4 flex gap-2 text-[#878787]">
        <TriangleAlert size={20} /> Veuillez renseigner la date de lancement et
        la durée du projet.
      </p>
    );
  }

  const handleDeviseChange = async (indicator, selected) => {
    if (selected !== "EUR") {
      await checkRate(devises?.filter((dev) => dev.code === selected)[0]);
    }
    setCurrencySelected(selected);
    setIndicators(
      indicators?.map((ind) => {
        if (ind.code === indicator.code) {
          return {
            ...ind,
            selectedCurrency: selected,
            selectedUnity: "",
          };
        }

        return ind;
      })
    );
  };

  // useEffect(() => {
  //   console.log("indicatorDataVerify changed", projectData?.country.currency);
  // }, [projectData?.country.currency.code]);
  const handleUnityChange = async (indicator, selected) => {
    setIndicators(
      indicators?.map((ind) => {
        if (ind.code === indicator.code) {
          return {
            ...ind,
            selectedUnity: selected,
          };
        }

        return ind;
      })
    );
  };

  const selectUnity = (indicator) => {
    const allUnity = indicator.unity.split("|");
    return (
      <select
        onChange={(e) => {
          handleUnityChange(indicator, e.target.value);
        }}
        className="absolute left-full w-max"
        name=""
        id=""
      >
        {allUnity.map((u, i) => {
          return (
            <option value={u} key={i}>
              {u}
            </option>
          );
        })}
      </select>
    );
  };

  return (
    <div>
      {openModalVerify && !loadingTreeFinancer && (
        <ModalVerify
          indicatorData={{
            openModal: openModalVerify,
            setOpenModalVerify: setOpenModalVerify,
            value: indicatorDataVerify?.value,
            pjs: indicatorDataVerify?.pjs,
            dateOldRate: indicatorDataVerify?.dateOldRate,
            desiredFundings: projectData?.desiredFunding,
            desiredFundingsSelected:
              indicatorDataVerify?.desiredFundingsSelected,
            isPrice: indicatorDataVerify?.isPrice,
            unity:
              indicatorDataVerify?.unityType == "price"
                ? currencySelected === "EUR"
                  ? "€"
                  : projectData?.country.currency.symbol
                : "",
            // : indicatorDataVerify?.unityType == "Nombre"
            // ? ""
            // : indicatorDataVerify?.unityType,
          }}
          data={treeFinancer}
        />
      )}
      <div className="calendar ">
        <div className="p-5 text-sm leading-relaxed">
          <p className="mb-4">
            Ce tableau présente le{" "}
            <strong>suivi mensuel des indicateurs</strong> liés au projet que vous financez. <br />
            Vous avez ici une <strong>vue complète et actualisée</strong> des
            performances sur toute l’année.
          </p>

          <p className="mb-2">
            Pour chaque indicateur, trois valeurs sont présentées mensuellement:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Prévu</strong> : objectifs fixés et programmés contractuellement ; 
              
            </li>
            <li>
              <strong>Réalisé</strong> : résultats effectivement atteints déclarés par le porteur de projet ; 
            </li>
            <li>
              <strong>Vérifié / justifié / certifié</strong> : données assorties d'un élément de preuve en pièce jointe, également disponible dans Mes documents. 
            </li>
          </ul>
          <p className="mt-4 text-gray-600 italic">
            Les données sont affichées à titre informatif. En tant que
            financeur, vous ne pouvez pas les modifier.
          </p>
        </div>
        <div className="sticky top-0 z-50  bg-background pb-4 pt-2">
          <div className=" flex justify-end pr-5">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                <Button
                  onClick={() => handleYearChange("prev")}
                  className="bg-background flex size-8 items-center justify-center rounded-full p-0 transition-all hover:bg-gray-100"
                  disabled={launchDate == (Number(yearParam) || year)}
                >
                  <ChevronLeft className="text-foreground" />
                </Button>
                <Button
                  onClick={() => handleYearChange("next")}
                  className="bg-background flex size-8 items-center justify-center rounded-full p-0 transition-all hover:bg-gray-100"
                  disabled={
                    launchDate - 1 + expectedDurationYear <=
                    (Number(yearParam) || year)
                  }
                >
                  <ChevronRight className="text-foreground" />
                </Button>
              </div>
              <Select onValueChange={handleSelectYear}>
                <SelectTrigger className="text-foreground/90 w-16 border-0 px-0 text-lg outline-none focus:ring-0 focus:ring-transparent">
                  <SelectValue placeholder={year} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {arrYear.map((i, k) => (
                      <SelectItem value={"" + (launchDate + k)} key={k}>
                        {launchDate + k}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div
            className="relative ml-auto mt-3 flex flex-wrap items-center gap-2"
            style={{ width: "calc(100% - 214px)" }}
          >
            <div className="flex w-full gap-2  pr-5">
              {listAllMonths.map((i, k) => (
                <div
                  className={`border-foreground/5 flex-1 border-x text-center `}
                  key={k}
                >
                  {i}
                </div>
              ))}
            </div>
          </div>
        </div>
        {error ? (
          <ErrorView error={error} />
        ) : (
          <div className="overflow-hidden">
            <div
              className="calendar-view mt-7  rounded-[20px] bg-[rgb(247,249,251)] p-[20px]"
              style={{
                willChange: "transform",
                transformStyle: "preserve-3d",
                userSelect: "none",
              }}
              ref={calendarRef}
            >
              {indicators?.map(
                (indicator, k: number) =>
                  indicator.id != 2 && indicator.id != 1 &&  (
                    <div className="mb-5 flex" id={indicator.name} key={k}>
                      <div className="w-[194px]">
                        <h2 className="relative mb-3 font-semibold text-[#434653]">
                          {indicator.name}

                          {indicator.unity === "price" && (
                            <select
                              onChange={(e) => {
                                handleDeviseChange(indicator, e.target.value);
                              }}
                              className="absolute left-full w-max"
                              name=""
                              id=""
                            >
                              <option
                                selected={
                                  indicator.selected === "EUR" ? true : false
                                }
                                value="EUR"
                                style={{
                                  color: "hsl(var(--foreground) / 0.3)",
                                }}
                              >
                                €
                              </option>
                              {projectData?.country !== undefined &&
                                projectData?.country !== null &&
                                projectData?.country.currency.code !==
                                  "EUR" && (
                                  <option
                                    selected={
                                      indicator.selected ===
                                      projectData?.country.currency.code
                                        ? true
                                        : false
                                    }
                                    value={projectData?.country.currency.code}
                                  >
                                    {projectData?.country.currency.symbol}
                                  </option>
                                )}
                            </select>
                          )}

                          {indicator.unity.includes("|") &&
                            selectUnity(indicator)}
                          {
                            indicator.unity !== "price" &&
                              !indicator.unity.includes("|") && (
                                <div className="absolute left-full top-0 flex w-max items-center gap-2">
                                  <Badge
                                    variant={"outline"}
                                    className=""
                                    style={{
                                      color: "hsl(var(--foreground) / 0.3)",
                                    }}
                                  >
                                    {(indicator.code === "tco2_sequestrees" ||
                                      (indicator.code.includes(
                                        "tco2_sequestrees-"
                                      ) &&
                                        indicator.correspondance != null)) && (
                                      <b className="text-orange-400">
                                        {parseFloat(
                                          (
                                            parseFloat(
                                              projectData?.indicatorValues?.find(
                                                (item) =>
                                                  item.indicator.id ===
                                                    indicator.id &&
                                                  item.type === 1 &&
                                                  item.year === year
                                              )?.yearValue
                                            ) || 0
                                          ).toFixed(1)
                                        ).toLocaleString("fr-FR")}
                                      </b>
                                    )}
                                    {(indicator.code === "tco2_sequestrees" ||
                                      (indicator.code.includes(
                                        "tco2_sequestrees-"
                                      ) &&
                                        indicator.correspondance != null)) && (
                                      <>&nbsp;</>
                                    )}

                                    {(indicator.code === "tco2_sequestrees" ||
                                      (indicator.code.includes(
                                        "tco2_sequestrees-"
                                      ) &&
                                        indicator.correspondance != null)) && (
                                      <b className="text-orange-400">
                                        {indicator.unity}
                                      </b>
                                    )}

                                    {indicator.code !== "tco2_sequestrees" &&
                                      (!indicator.code.includes(
                                        "tco2_sequestrees-"
                                      ) ||
                                        (indicator.code.includes(
                                          "tco2_sequestrees-"
                                        ) &&
                                          indicator.correspondance ==
                                            null)) && <>{indicator.unity}</>}

                                    {(indicator.code === "tco2_sequestrees" ||
                                      (indicator.code.includes(
                                        "tco2_sequestrees-"
                                      ) &&
                                        indicator.correspondance != null)) && (
                                      <>&nbsp;prévu en {year}</>
                                    )}
                                  </Badge>
                                  {(indicator.code === "tco2_sequestrees" ||
                                    (indicator.code.includes(
                                      "tco2_sequestrees-"
                                    ) &&
                                      indicator.correspondance != null)) && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Info
                                          size={18}
                                          className="cursor-pointer"
                                          style={{
                                            color:
                                              "hsl(var(--foreground) / 0.3)",
                                          }}
                                        />
                                      </PopoverTrigger>
                                      <PopoverContent className="z-[99999] text-xs">
                                        <p className="m-0">
                                          Un arbre absorbe en moyenne{" "}
                                          <b>
                                            {
                                              projectData?.indicatorValues?.find(
                                                (item) =>
                                                  item.indicator.id ===
                                                    indicator.id &&
                                                  item.type === 1 &&
                                                  item.year === year
                                              )?.unitValue
                                            }
                                          </b>
                                          &nbsp;{indicator.unity} par an pendant{" "}
                                          <b>
                                            {
                                              projectData?.indicatorValues?.find(
                                                (item) =>
                                                  item.indicator.id ===
                                                    indicator.id &&
                                                  item.type === 1 &&
                                                  item.year === year
                                              )?.unitYearValue
                                            }{" "}
                                            ans
                                          </b>
                                          &nbsp;
                                        </p>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              ) /*<UnityInput indicator={indicator} setProject={setProject} setIndicators={setIndicators} />*/
                          }
                        </h2>
                        <p className="mb-[.35rem] flex -translate-y-1 justify-between pr-2">
                          Prévu
                        </p>
                        <p className="mb-[.35rem] flex -translate-y-1 justify-between pr-2">
                          Réalisé
                        </p>
                        <p className="mb-0 flex -translate-y-1 justify-between pr-2">
                          Verifié / justifié / certifié
                        </p>
                      </div>

                      <div className="mt-8 flex flex-1 flex-col justify-end">
                        <div className="flex flex-col">
                          <div className="mb-1 flex w-full gap-2 overflow-auto">
                            {arrMonth.map((_, k) => {
                              const filteredIndicators =
                                projectData?.indicatorValues?.filter(
                                  (item) =>
                                    item.indicator.id === indicator.id &&
                                    item.type === 1 &&
                                    item.year === year &&
                                    item.month === k
                                ) ?? [];

                              return filteredIndicators?.length > 0 ? (
                                filteredIndicators.map((item, key: number) => (
                                  <FieldInput
                                    key={`${item.indicator.id}-${item.year}-${item.month}`}
                                    defaultValue={item.value}
                                    indicatorData={{
                                      project: projectData?.id as number,
                                      indicator: item.indicator.id,
                                      isPrice: item.indicator.unity === "price",
                                      type: item.type,
                                      value: item.value,
                                      desiredFundingsSelected:
                                        item.desiredFundings,
                                      year: item.year,
                                      month: item.month,
                                      currency: devises.find(
                                        (d) =>
                                          d.code === indicator.selectedCurrency
                                      ),
                                      unity: indicator.selectedUnity,
                                      unityType: indicator.unity,
                                      oldRate: indicator.oldRate,
                                    }}
                                    isReadOnly={true}
                                    isDisabled={
                                      (k < launchMonthDate &&
                                        year <= launchDate) ||
                                      ((Number(year) ===
                                        dateWithAddedMonths?.getFullYear() &&
                                        k > dateWithAddedMonths?.getMonth()) ??
                                        0)
                                    }
                                    devises={devises}
                                    fetchConstraints={fetchConstraints}
                                    valueInserted={item?.valueInserted ?? null}
                                    dateOldRate={item?.dateOldRate ?? null}
                                    oldRate={item?.oldRate ?? null}
                                  />
                                ))
                              ) : (
                                <FieldInput
                                  key={`${k}-${year}-${indicator.id}`}
                                  defaultValue={
                                    indicator.defaultValue !== 0
                                      ? indicator.defaultValue
                                      : "-"
                                  }
                                  indicatorData={{
                                    project: projectData?.id as number,
                                    indicator: indicator.id,
                                    isPrice: indicator.unity === "price",
                                    type: 1,
                                    value: null,
                                    desiredFundingsSelected: [],
                                    year,
                                    month: k, // ✅ aligne sur le filtrage (1..12)
                                    currency: devises.find(
                                      (d) =>
                                        d.code === indicator.selectedCurrency
                                    ),
                                    unity: indicator.selectedUnity,
                                    unityType: indicator.unity,
                                  }}
                                  isReadOnly={true}
                                  isDisabled={
                                    (k < launchMonthDate &&
                                      year <= launchDate) ||
                                    (Number(year) ===
                                      dateWithAddedMonths?.getFullYear() &&
                                      k >
                                        (dateWithAddedMonths?.getMonth() ?? 0))
                                  }
                                  devises={devises}
                                  fetchConstraints={fetchConstraints}
                                  valueInserted={3}
                                  dateOldRate={3}
                                  oldRate={3}
                                />
                              );
                            })}
                          </div>

                          <div className="mb-1 flex w-full gap-2 overflow-auto">
                            {arrMonth.map((i, k) => {
                              const filteredIndicators =
                                projectData?.projectIndicators?.filter(
                                  (item) =>
                                    item.indicator.id === indicator.id &&
                                    item.type === 2 &&
                                    item.year === year &&
                                    item.month === k
                                );
                              const isFutur =
                                new Date().getFullYear() < Number(yearParam) ||
                                (new Date().getFullYear() ===
                                  Number(yearParam) &&
                                  k > new Date().getMonth());
                              // 🔍 Si on ne trouve pas dans projectIndicators,
                              // on vérifie si indicator.code === "tco2_sequestrees"
                              // et on cherche dans project.indicatorsValue
                              const alternativeIndicators =
                                indicator.code === "tco2_sequestrees" ||
                                indicator.code.includes("tco2_sequestrees-")
                                  ? projectData?.indicatorValues?.filter(
                                      (item) =>
                                        item.indicator.id === indicator.id &&
                                        item.type === 2 &&
                                        item.year === year &&
                                        item.month === k
                                    )
                                  : [];

                              // On détermine quelle source utiliser
                              const sourceIndicators =
                                filteredIndicators?.length > 0
                                  ? filteredIndicators
                                  : alternativeIndicators?.length > 0
                                  ? alternativeIndicators
                                  : [];

                              // S'il y a des valeurs (filtered ou alternative)
                              if (sourceIndicators.length > 0) {
                                return sourceIndicators.map(
                                  (item, key: number) => (
                                    <FieldInput
                                      defaultValue={item.value}
                                      key={`${item.indicator.id}-${item.year}-${item.month}`}
                                      indicatorData={{
                                        project: projectData?.id as number,
                                        indicator: item.indicator.id,
                                        isPrice:
                                          item.indicator.unity === "price",
                                        type: item.type,
                                        value: item.value,
                                        desiredFundingsSelected:
                                          item.desiredFundings,
                                        obtainedFundingsSelected:
                                          item.obtainedFundings,
                                        year: item.year,
                                        month: item.month,
                                        currency: devises.find(
                                          (dev) =>
                                            dev.code ===
                                            indicator.selectedCurrency
                                        ),
                                        unity: indicator.selectedUnity,
                                        unityType: indicator.unity,
                                        oldRate: indicator.oldRate,
                                      }}
                                      isReadOnly={true}
                                      isDisabled={
                                        (k < launchMonthDate &&
                                          year <= launchDate) ||
                                        (Number(year) ==
                                          dateWithAddedMonths?.getFullYear() &&
                                          k > dateWithAddedMonths?.getMonth())
                                      }
                                      devises={devises}
                                      fetchConstraints={fetchConstraints}
                                      valueInserted={
                                        item?.valueInserted ?? null
                                      }
                                      dateOldRate={item?.dateOldRate ?? null}
                                      oldRate={item?.oldRate ?? null}
                                      isFutur={isFutur}
                                    />
                                  )
                                );
                              }

                              // Sinon on affiche le champ vide
                              return (
                                <FieldInput
                                  defaultValue={"-"}
                                  key={`${k}-${year}-${indicator.id}`}
                                  indicatorData={{
                                    project: projectData?.id as number,
                                    indicator: indicator.id,
                                    isPrice: indicator.unity === "price",
                                    type: 2,
                                    value: null,
                                    desiredFundingsSelected: [],
                                    year: year,
                                    month: k,
                                    currency: devises.find(
                                      (dev) =>
                                        dev.code === indicator.selectedCurrency
                                    ),
                                    unity: indicator.selectedUnity,
                                    unityType: indicator.unity,
                                  }}
                                  isReadOnly={true}
                                  isDisabled={
                                    (k < launchMonthDate &&
                                      year <= launchDate) ||
                                    (Number(year) ==
                                      dateWithAddedMonths?.getFullYear() &&
                                      k > dateWithAddedMonths?.getMonth())
                                  }
                                  devises={devises}
                                  fetchConstraints={fetchConstraints}
                                  valueInserted={2}
                                  dateOldRate={2}
                                  oldRate={2}
                                  isFutur={isFutur}
                                />
                              );
                            })}
                          </div>

                          <div className="flex w-full gap-2 overflow-visible">
                            {arrMonth.map((i, k) => {
                              const filteredIndicators =
                                projectData?.projectIndicators?.filter(
                                  (item) =>
                                    item.indicator.id === indicator.id &&
                                    item.type === 3 &&
                                    item.year === year &&
                                    item.month === k
                                );
                              const isFutur =
                                new Date().getFullYear() < Number(yearParam) ||
                                (new Date().getFullYear() ===
                                  Number(yearParam) &&
                                  k > new Date().getMonth());
                              // 🔍 Si aucun résultat, on regarde dans project.indicatorValues
                              // si indicator.code correspond à tco2_sequestrees ou à une variante
                              const alternativeIndicators =
                                indicator.code === "tco2_sequestrees" ||
                                indicator.code.includes("tco2_sequestrees-")
                                  ? projectData?.indicatorValues?.filter(
                                      (item) =>
                                        item.indicator.id === indicator.id &&
                                        item.type === 3 &&
                                        item.year === year &&
                                        item.month === k
                                    )
                                  : [];

                              // ✅ On choisit la bonne source de données
                              const sourceIndicators =
                                filteredIndicators?.length > 0
                                  ? filteredIndicators
                                  : alternativeIndicators?.length > 0
                                  ? alternativeIndicators
                                  : [];

                              // 🟢 Si des valeurs existent (dans filtered ou alternative)
                              if (sourceIndicators.length > 0) {
                                return sourceIndicators.map(
                                  (item, key: number) => (
                                    <FieldInput
                                      defaultValue={item.value}
                                      key={`${item.indicator.id}-${item.year}-${item.month}`}
                                      indicatorData={{
                                        project: projectData?.id as number,
                                        indicator: item.indicator.id,
                                        isPrice:
                                          item.indicator.unity === "price",
                                        type: item.type,
                                        value: item.value,
                                        desiredFundingsSelected:
                                          item.desiredFundings,
                                        year: item.year,
                                        month: item.month,
                                        currency: devises.find(
                                          (dev) =>
                                            dev.code ===
                                            indicator.selectedCurrency
                                        ),
                                        unity: indicator.selectedUnity,
                                        unityType: indicator.unity,
                                        pjs: item.pjs,
                                        setIndicatorDataVerify:
                                          setIndicatorDataVerify,
                                        setOpenModalVerify: setOpenModalVerify,
                                      }}
                                      isReadOnly={true}
                                      isDisabled={
                                        item.month < k ||
                                        (Number(year) ==
                                          dateWithAddedMonths?.getFullYear() &&
                                          k > dateWithAddedMonths?.getMonth())
                                      }
                                      devises={devises}
                                      fetchConstraints={fetchConstraints}
                                      valueInserted={
                                        item?.valueInserted ?? null
                                      }
                                      dateOldRate={item?.dateOldRate ?? null}
                                      oldRate={item?.oldRate ?? null}
                                      isFutur={isFutur}
                                    />
                                  )
                                );
                              }

                              // 🔴 Sinon : aucun indicateur → on affiche un champ vide
                              return (
                                <FieldInput
                                  defaultValue={"-"}
                                  key={`${k}-${year}-${indicator.id}`}
                                  indicatorData={{
                                    project: projectData?.id as number,
                                    indicator: indicator.id,
                                    isPrice: indicator.unity === "price",
                                    type: 3,
                                    value: null,
                                    desiredFundingsSelected: [],
                                    year: year,
                                    month: k,
                                    currency: devises.find(
                                      (dev) =>
                                        dev.code === indicator.selectedCurrency
                                    ),
                                    unity: indicator.selectedUnity,
                                    unityType: indicator.unity,
                                    pjs: [],
                                    setIndicatorDataVerify:
                                      setIndicatorDataVerify,
                                    setOpenModalVerify: setOpenModalVerify,
                                  }}
                                  isReadOnly={true}
                                  isDisabled={
                                    (k < launchMonthDate &&
                                      year <= launchDate) ||
                                    (Number(year) ==
                                      dateWithAddedMonths?.getFullYear() &&
                                      k > dateWithAddedMonths?.getMonth())
                                  }
                                  devises={devises}
                                  fetchConstraints={fetchConstraints}
                                  valueInserted={1}
                                  dateOldRate={1}
                                  oldRate={1}
                                  isFutur={isFutur}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ModalVerify = ({ indicatorData, data }) => {
  const [fsSelected, setFsSelected] = useState(
    indicatorData.desiredFundingsSelected == undefined
      ? []
      : indicatorData.desiredFundingsSelected
  );

  return (
    <Dialog open={true} onOpenChange={indicatorData.setOpenModalVerify}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Valeur de l'indicateur{" "}
            <span className="text-primary">vérifié / justifié / certifié</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap">
          <div className="w-full pr-4">
            <div className="mb-4">
              <div className="w-36 bg-gray-200 rounded-md py-3 px-4">
                {indicatorData.value} {indicatorData.unity}
              </div>
            </div>
            <label className="mb-2 block text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Justificatifs
            </label>
            <Suspense fallback={<p> loading...</p>}>
              <div className="grid grid-cols-5 gap-2">
                {indicatorData.pjs &&
                  indicatorData.pjs?.map((pj, key) => {
                    const extension = pj.name.split(".").pop()?.toLowerCase();
                    const isImg = isImage(extension);
                    const isOffice = isOffices(extension);
                    const fileUrl = import.meta.env.VITE_MEDIA_BASE + pj.uri;

                    return (
                      <div
                        className="size-full aspect-square overflow-hidden rounded-md border border-input bg-gray-200 group"
                        key={pj.id ?? key}
                      >
                        {isImg || pj.name == "blob" ? (
                          <ImageThumbnail
                            url={fileUrl}
                            treeFinancer={data}
                            pj={pj}
                            noMore={true}
                          />
                        ) : extension === "pdf" ? (
                          <PdfThumbnail
                            url={fileUrl}
                            extension={extension}
                            treeFinancer={data}
                            pj={pj}
                            noMore={true}
                          />
                        ) : isOffice ? (
                          <OfficeThumbnail
                            srcUrl={fileUrl}
                            extension={extension}
                            treeFinancer={data}
                            pj={pj}
                            noMore={true}
                          />
                        ) : (
                          <div className="text-sm text-muted-foreground text-center px-2">
                            Aperçu non disponible
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </Suspense>
            {indicatorData.isPrice && (
              <div className="w-full rounded-[20px] bg-card mt-4  py-3">
                <h2 className="mb-2 text-xl font-bold">
                  Lien avec les financements souhaités
                </h2>

                <div className="mt-4 flex flex-wrap">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="">Titre</TableHead>
                        <TableHead>Montant</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {indicatorData.desiredFundings
                        .filter(
                          (df) =>
                            df.status !== 1 ||
                            indicatorData.desiredFundingsSelected.includes(
                              df.id
                            )
                        )
                        .map((df) => (
                          <TableRow
                            key={df.id}
                            className={
                              fsSelected.includes(df.id) ? " bg-gray-200" : ""
                            }
                          >
                            <TableCell className="font-medium flex gap-2 items-center">
                              <input
                                type="checkbox"
                                checked={fsSelected.includes(df.id)}
                              />
                              {df.title}
                            </TableCell>
                            <TableCell>
                              {df.amount.toLocaleString("fr")}{" "}
                              {df.devise.symbol}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={() => indicatorData.setOpenModalVerify(false)}
          >
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ErrorView = ({ error }: { error: string }) => {
  return (
    <div className="flex flex-col items-center pt-20 text-center">
      <h1 className="text-4xl font-bold text-red-600">Oups !</h1>
      <p className="mt-4 text-lg">Une erreur s&apos;est produite.</p>
      <p className="mt-2 text-gray-600">{error}</p>
      <button
        onClick={() => window.location.reload()}
        className="mt-6 rounded-lg bg-blue-500 px-4 py-2 text-white transition hover:bg-blue-600"
      >
        Réessayer
      </button>
    </div>
  );
};

export default PerformanceDetail;
